// Hosted videos share source selection and visibility handling. Interactive videos
// retain the viewer's playback intent, position and sound preferences.
// Source URLs stay inert in data-src so the preload scanner cannot fetch them.
class ViewportVideo extends HTMLElement {
  static get observedAttributes() { return ['data-inactive']; }

  attributeChangedCallback() { if (this.abortController) this.sync(); }

  connectedCallback() {
    if (this.abortController) return;
    // Async registration can happen while the HTML parser is still building a
    // player. Wait for every source, so a partial list cannot start a wrong file.
    if (this.hasAttribute('data-await-markup') && !this.querySelector(':scope > template[data-video-ready]')) {
      if (!this.markupObserver) {
        this.markupObserver = new MutationObserver(() => this.connectedCallback());
        this.markupObserver.observe(this, {childList: true});
      }
      return;
    }
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.sources = [...this.video.querySelectorAll('source[data-src]')].map((source) => ({
      url: source.dataset.src,
      type: source.type,
      width: Number(source.dataset.width) || 0,
      media: source.getAttribute('media'),
    }));
    this.abortController = new AbortController();
    const {signal} = this.abortController;
    this.inViewport = false;
    this.playGeneration ??= 0;
    this.loadedSource = '';
    this.failedSource = '';
    this.playPending = false;
    this.interactive = this.hasAttribute('data-interactive');
    this.autoplay = this.dataset.autoplay !== 'false';
    this.expectedPauses = 0;
    this.cardMedia = this.closest('.card__media');
    this.cardVideo = this.closest('[data-card-video]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onStateChange = () => { if (!signal.aborted) this.sync(); };
    this.reducedMotion.addEventListener('change', this.onStateChange, {signal});
    document.addEventListener('visibilitychange', this.onStateChange, {signal});
    window.addEventListener('resize', this.onStateChange, {signal, passive: true});
    if (this.interactive) {
      this.video.addEventListener('play', () => {
        // load() can discard a queued pause event during an unload. A new play
        // starts a fresh cycle so the next native pause is never swallowed.
        this.expectedPauses = 0;
        if (!this.playPending) {
          this.intent = 'play';
          this.failedSource = '';
        }
      }, {signal});
      this.video.addEventListener('pause', () => {
        if (this.expectedPauses > 0) this.expectedPauses--;
        else if (this.loadedSource && !this.video.ended) this.intent = 'pause';
        this.video.removeAttribute('data-video-playing');
      }, {signal});
      this.video.addEventListener('ended', () => {
        if (!this.video.loop) this.intent = 'pause';
      }, {signal});
      this.video.addEventListener('loadedmetadata', () => {
        if (this.savedTime > 0) {
          this.video.currentTime = Math.min(this.savedTime, this.video.duration || this.savedTime);
        }
      }, {signal});
    }
    this.video.addEventListener('playing', () => {
      if (this.canPlay()) this.video.setAttribute('data-video-playing', '');
      else this.stop(!this.canLoad());
    }, {signal});
    this.video.addEventListener('error', () => {
      if (!this.loadedSource || !this.video.error) return;
      this.failedSource = this.loadedSource;
      this.stop(true);
    }, {signal});

    // Gallery and decoded rollover transitions can cover a video while its card
    // remains in view. Observe their state classes, not the drag animation styles.
    if (this.cardMedia) {
      this.galleryObserver = new MutationObserver(this.onStateChange);
      this.galleryObserver.observe(this.cardMedia, {
        attributes: true, attributeFilter: ['class'], childList: true, subtree: true,
      });
    }
    if ('ResizeObserver' in window) {
      this.sizeObserver = new ResizeObserver(this.onStateChange);
      this.sizeObserver.observe(this.video);
    }
    if ('IntersectionObserver' in window) {
      // Edge contact counts as intersecting even with no visible pixels. A
      // positive threshold also reports when a carousel moves that edge inward.
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (signal.aborted) return;
        const entry = entries[entries.length - 1];
        this.inViewport = entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0;
        this.sync();
      }, {rootMargin: '0px', threshold: [0, Number.EPSILON]});
      this.visibilityObserver.observe(this.video);
    }
    // Native controls must remain usable on older browsers without observation.
    if (!('IntersectionObserver' in window) && this.interactive) {
      this.inViewport = true;
      this.sync();
    }
  }

  disconnectedCallback() {
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.abortController?.abort();
    this.abortController = null;
    this.visibilityObserver?.disconnect();
    this.sizeObserver?.disconnect();
    this.galleryObserver?.disconnect();
    this.inViewport = false;
    if (this.video) this.stop(true);
  }

  canLoad() {
    return this.isConnected && this.inViewport && !document.hidden && !this.hasAttribute('data-inactive') &&
      (this.interactive || !this.reducedMotion.matches);
  }

  canPlay() {
    return this.canLoad() && this.wantsPlayback() && !this.cardVideo?.classList.contains('card__video--hidden') &&
      !this.cardMedia?.querySelector('.card__rollover-image--visible');
  }

  wantsPlayback() {
    if (this.interactive && this.intent) return this.intent === 'play';
    return this.autoplay && !this.reducedMotion.matches;
  }

  requestPlay() {
    this.intent = 'play';
    this.failedSource = '';
    this.sync();
    return this.playPromise || Promise.resolve();
  }

  requestPause() {
    this.intent = 'pause';
    this.stop(false);
  }

  selectSource() {
    const eligible = this.sources.filter((source) => source.url &&
      (!source.media || window.matchMedia(source.media).matches) &&
      (!source.type || this.video.canPlayType(source.type)));
    // MP4 renditions expose useful dimensions and work without an HLS library.
    const mp4 = eligible.filter((source) => source.type === 'video/mp4');
    const candidates = mp4.length ? mp4 : eligible;
    const cap = window.matchMedia('(max-width: 749px)').matches ? 720 : 1280;
    const sized = candidates.filter((source) => source.width > 0).sort((a, b) => a.width - b.width);
    const bounded = sized.filter((source) => source.width <= cap);
    const target = Math.min(cap, this.video.getBoundingClientRect().width * Math.min(window.devicePixelRatio || 1, 2));
    return bounded.find((source) => source.width >= target) || bounded.at(-1) || sized[0] || candidates[0];
  }

  sync() {
    if (!this.canLoad()) {
      this.stop(true);
      return;
    }
    if (!this.interactive && !this.canPlay()) {
      // Retain the decoded frame during gallery/rollover fades. Offscreen and
      // reduced-motion states above also detach the source to stop buffering.
      this.stop(false);
      return;
    }
    const source = this.selectSource();
    if (!source || source.url === this.failedSource) {
      this.stop(true);
      return;
    }
    if (this.loadedSource !== source.url) {
      this.stop(true);
      if (!this.interactive) {
        this.video.muted = true;
        this.video.defaultMuted = true;
      }
      this.loadedSource = source.url;
      this.video.src = source.url;
      // Assigning src prepares native controls. Explicit load() can fetch despite
      // preload=none in Chrome, so manual videos wait for the viewer's play.
      if (!this.interactive || this.wantsPlayback()) this.video.load();
    }
    if (!this.canPlay()) {
      this.stop(false);
      return;
    }
    if (!this.video.paused || this.playPending) return;
    const generation = ++this.playGeneration;
    this.playPending = true;
    try {
      this.playPromise = Promise.resolve(this.video.play()).then(() => {
        if (generation !== this.playGeneration) return;
        this.playPending = false;
        if (!this.canPlay()) this.stop(!this.canLoad());
      }).catch(() => this.handlePlayFailure(generation, source.url));
    } catch {
      this.handlePlayFailure(generation, source.url);
    }
  }

  handlePlayFailure(generation, source) {
    if (generation !== this.playGeneration) return;
    if (this.interactive) {
      // A blocked autoplay attempt must leave the controls available for a gesture.
      this.intent = 'pause';
      this.stop(false);
    } else {
      this.failedSource = source;
      this.stop(true);
    }
  }

  stop(unload) {
    this.playGeneration++;
    this.playPending = false;
    if (this.interactive && this.loadedSource && this.video.readyState > 0) {
      this.savedTime = this.video.ended ? 0 : this.video.currentTime;
    }
    if (!this.video.paused) {
      if (this.interactive) this.expectedPauses++;
      this.video.pause();
    }
    this.video.removeAttribute('data-video-playing');
    if (unload && this.video.hasAttribute('src')) {
      this.loadedSource = '';
      this.video.removeAttribute('src');
      this.video.load();
    }
  }
}

// Gallery owners expose their active cells through classes. Keep a neighboring
// carousel cell inert even when a small part of it intersects the viewport.
class ProductVideoGallery extends HTMLElement {
  connectedCallback() {
    if (this.videoGalleryAbort) return;
    this.videoGalleryAbort = new AbortController();
    this.syncVideos = () => {
      for (const player of this.querySelectorAll('viewport-video')) {
        const slide = player.closest('[data-slide]');
        const slider = slide?.closest('[data-product-media-slider]');
        const owlItem = player.closest('.owl-item');
        let inactive = false;
        if (slide) {
          inactive = slide.classList.contains('main-product__media-slide--hidden') || slide.hidden;
          if (slider?.classList.contains('flickity-enabled')) {
            const visibleSlides = [...slider.querySelectorAll('[data-slide]')].filter(cell => !cell.hidden && !cell.classList.contains('main-product__media-slide--hidden'));
            // Colour filtering leaves zero-width cells in Flickity's index. Its
            // selected raw cell can be hidden while the first visible cell paints.
            const active = visibleSlides.find(cell => cell.classList.contains('is-selected')) || visibleSlides[0];
            inactive ||= slide !== active;
          } else if (window.matchMedia('(max-width: 989px)').matches) inactive ||= !slide.hasAttribute('data-initial-media');
        }
        if (owlItem) inactive ||= !owlItem.classList.contains('active');
        player.toggleAttribute('data-inactive', inactive);
      }
    };
    this.videoGalleryObserver = new MutationObserver(this.syncVideos);
    this.videoGalleryObserver.observe(this, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden']});
    window.addEventListener('resize', this.syncVideos, {signal: this.videoGalleryAbort.signal, passive: true});
    this.syncVideos();
  }
  disconnectedCallback() {
    this.videoGalleryAbort?.abort();
    this.videoGalleryAbort = null;
    this.videoGalleryObserver?.disconnect();
  }
}
customElements.get('product-video-gallery') || customElements.define('product-video-gallery', ProductVideoGallery);

if (!customElements.get('viewport-video')) customElements.define('viewport-video', ViewportVideo);
